import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Search, Clock, CheckCircle, Shield, Star, Users, Award,
  ArrowRight, Phone, MessageSquare, Wrench
} from 'lucide-react';

const HowItWorksPage = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: '01',
      title: 'Hizmet Seçin',
      description: 'İhtiyacınız olan hizmeti kategorilerden seçin. Kombi, klima, beyaz eşya veya elektrik - ne olursa olsun.',
      icon: Search,
      color: 'bg-[#006D77]'
    },
    {
      number: '02',
      title: 'Detayları Girin',
      description: 'Sorununuzu birkaç tıkla anlatın. Acil mi yoksa ileri tarihli mi? Adresinizi girin.',
      icon: MessageSquare,
      color: 'bg-[#E29578]'
    },
    {
      number: '03',
      title: 'Usta Gelsin',
      description: 'Size en uygun usta atanır ve randevu saatinde kapınıza gelir. İşiniz garantili şekilde tamamlanır.',
      icon: Wrench,
      color: 'bg-[#059669]'
    }
  ];

  const benefits = [
    { icon: Shield, title: 'İş Garantisi', desc: 'Tüm hizmetler 30 gün garanti altında' },
    { icon: Clock, title: 'Aynı Gün Hizmet', desc: 'Acil işleriniz için aynı gün servis' },
    { icon: Star, title: 'Uzman Ustalar', desc: 'Deneyimli ve sertifikalı teknisyenler' },
    { icon: Users, title: '500+ Usta', desc: 'Geniş usta ağı ile hızlı eşleşme' },
    { icon: Phone, title: '7/24 Destek', desc: 'Her zaman ulaşılabilir müşteri hizmetleri' },
    { icon: Award, title: 'Uygun Fiyat', desc: 'Rekabetçi ve şeffaf fiyatlandırma' }
  ];

  const testimonials = [
    { name: 'Ahmet Y.', text: 'Kombim bozulmuştu, aynı gün geldiler ve hallettiler. Çok memnunum!', rating: 5, service: 'Kombi Servisi' },
    { name: 'Zeynep K.', text: 'Klimamın gazı bitmişti, hızlıca doldurdular. Fiyat da uygundu.', rating: 5, service: 'Klima Servisi' },
    { name: 'Mehmet A.', text: 'Çamaşır makinem arızalanmıştı, usta çok ilgiliydi ve sorunu çözdü.', rating: 5, service: 'Beyaz Eşya' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-[#0f172a] to-[#1e293b] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#006D77] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#E29578] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-[#006D77]/20 text-[#006D77] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Çok Basit, Çok Hızlı
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              3 Adımda Evinize<br />
              <span className="text-[#E29578]">Usta Gelsin</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              Karmaşık süreçler yok, sadece birkaç tık. İhtiyacınız olan hizmeti seçin, gerisini bize bırakın.
            </p>
            <Button
              onClick={() => navigate('/hizmetler')}
              className="bg-[#E29578] hover:bg-[#D67A56] text-white rounded-full px-8 py-4 text-lg font-semibold"
            >
              Hemen Başla
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Adımlar */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="relative">
                  {/* Bağlantı Çizgisi */}
                  {idx < steps.length - 1 && (
                    <div className="hidden md:block absolute top-16 left-[60%] w-full h-1 bg-gradient-to-r from-gray-200 to-transparent"></div>
                  )}
                  
                  <div className="text-center">
                    <div className="relative inline-block mb-6">
                      <div className={`${step.color} w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg`}>
                        <Icon className="w-10 h-10 text-white" />
                      </div>
                      <span className="absolute -top-2 -right-2 bg-white text-gray-900 font-bold text-sm w-8 h-8 rounded-full flex items-center justify-center shadow-md">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-500 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avantajlar */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden Bizi Tercih Etmelisiniz?
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              HizmetGeliyo ile ev hizmetleri almak hiç bu kadar kolay olmamıştı
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="bg-[#E0F2F4] w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#006D77]" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{benefit.title}</h4>
                  <p className="text-gray-500 text-sm">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Müşteri Yorumları */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Müşterilerimiz Ne Diyor?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(item.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{item.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{item.name}</span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{item.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#006D77] to-[#0A9396]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Hizmet Almaya Hazır mısınız?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Dakikalar içinde talep oluşturun, uzman ustalar size ulaşsın
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/hizmetler')}
              className="bg-white text-[#006D77] hover:bg-gray-100 rounded-full px-8 py-4 font-semibold"
            >
              Hizmetleri Gör
            </Button>
            <Button
              onClick={() => navigate('/prousta')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#006D77] rounded-full px-8 py-4 font-semibold bg-transparent"
            >
              Usta Girişi
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
